import { Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import {
    deleteNavigationElement as deleteNavElement,
    getNavigationElements as getNavElements,
} from '../../../sources/flow';
import translations from '../../../translations';
import type { FlowGraphMapElementResponseAPI } from '../../../types/graph';
import type { NavigationElementResponseAPI } from '../../../types/navigation';
import ButtonDanger from '../../buttons/ButtonDanger';
import ButtonDefault from '../../buttons/ButtonDefault';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import Glyphicon from '../../generic/Glyphicon';
import Modal from '../../generic/modal/GenericModal';
import Table from '../../generic/Table';
import Loader from '../../loader/Loader';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import NavigationEditor, { type NavigationType } from './NavigationEditor';

type Props = {
    container: HTMLElement | null;
    close: () => void;
    flowId: string;
    mapElements: FlowGraphMapElementResponseAPI[];
};

type EditorState = {
    isOpen: boolean;
    navigationType: NavigationType | null;
};

const NavigationModal = ({ flowId, close, container, mapElements }: Props) => {
    const [navigationElements, setNavigationElements] = useState<NavigationElementResponseAPI[]>(
        [],
    );
    const [editingId, setEditingId] = useState<string | null>(null);

    const [showingEditPage, setShowingEditPage] = useState<EditorState>({
        isOpen: false,
        navigationType: null,
    });
    const [showingDeletePage, setShowingDeletePage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const dispatchNotification = useNotificationDispatch();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        getNavigationData();
    }, []);

    const getNavigationData = async () => {
        try {
            const response = await getNavElements({
                flowId: flowId,
            });

            setNavigationElements(response);
            setIsLoading(false);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }
    };

    const deleteNavigationElement = async (id: string | null) => {
        if (id) {
            try {
                await deleteNavElement({
                    flowId: flowId,
                    navigationElementId: id,
                });

                refreshData();
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        }
    };

    const findNavigationName = (id: string | null) => {
        if (id) {
            const foundElement = navigationElements.find((element) => {
                return element.id === id;
            });

            return foundElement?.developerName;
        }

        return undefined;
    };

    const refreshData = () => {
        closeEditor();
        setIsLoading(true);
        getNavigationData();
    };

    const showEditor = (id: string, navigationType: NavigationType) => {
        setEditingId(id);
        setShowingEditPage({ isOpen: true, navigationType });
    };

    const showDeleteConfirmation = (id: string) => {
        setEditingId(id);
        setShowingDeletePage(true);
    };

    const closeEditor = () => {
        setEditingId(null);
        setShowingEditPage({ isOpen: false, navigationType: null });
        setShowingDeletePage(false);
    };

    const renderLoadingSection = <Loader message="Loading navigations table" />;

    const mainPageHeader = () => {
        return <h4 className="modal-title">Navigations</h4>;
    };

    const mainPageBody = () => {
        return isLoading ? (
            renderLoadingSection
        ) : (
            <div className="table-container">
                <div className="items-header">
                    <ButtonDefault className="btn-sm" onClick={refreshData}>
                        <Glyphicon glyph="refresh" />
                    </ButtonDefault>
                    <ButtonPrimary
                        className="btn-sm header-btn"
                        title="New Navigation"
                        onClick={() =>
                            setShowingEditPage({ isOpen: true, navigationType: 'NAVIGATION' })
                        }
                    >
                        <Glyphicon glyph="plus" />
                        <span>New Navigation</span>
                    </ButtonPrimary>
                    <ButtonPrimary
                        className="btn-sm header-btn"
                        title="New Progress Tracker"
                        onClick={() =>
                            setShowingEditPage({
                                isOpen: true,
                                navigationType: 'PROGRESSTRACKER',
                            })
                        }
                    >
                        <Glyphicon glyph="plus" />
                        <span>New Progress Tracker</span>
                    </ButtonPrimary>
                </div>
                <Table
                    items={navigationElements}
                    columns={[
                        {
                            renderHeader: () => translations.COMMON_TABLE_name,
                            renderCell: ({ item }) => (
                                <button
                                    className="link-emulate"
                                    onClick={() =>
                                        showEditor(
                                            item.id,
                                            item.progressTracker ? 'PROGRESSTRACKER' : 'NAVIGATION',
                                        )
                                    }
                                    type="button"
                                >
                                    {item.developerName}
                                </button>
                            ),
                        },
                        {
                            renderHeader: () => translations.COMMON_TABLE_summary,
                            renderCell: ({ item }) => <div>{item.developerSummary}</div>,
                        },
                        {
                            renderHeader: () => translations.COMMON_TABLE_actions,
                            renderCell: ({ item }) => (
                                <div className="action-btn-wrapper">
                                    <button
                                        title={`Delete ${item.developerName}`}
                                        className="table-icon table-icon-delete"
                                        aria-label={`Delete ${item.developerName}`}
                                        onClick={() => showDeleteConfirmation(item.id)}
                                        type="button"
                                    >
                                        <Trash />
                                    </button>
                                </div>
                            ),
                            size: '5rem',
                        },
                    ]}
                    pagination={true}
                />
            </div>
        );
    };

    const mainPageFooter = () => {
        return (
            <ButtonDefault title="Close Modal" onClick={close}>
                Close
            </ButtonDefault>
        );
    };

    const deletePageHeader = () => {
        return <h4 className="modal-title">Delete Navigation</h4>;
    };

    const deletePageBody = () => {
        return (
            <>
                <h2>Confirm Delete</h2>
                <p data-testid="delete-subtext">
                    Are you sure you want to delete the Navigation '{findNavigationName(editingId)}
                    '? <strong>This cannot be undone.</strong>
                </p>
            </>
        );
    };

    const deletePageFooter = () => {
        return (
            <>
                <ButtonDefault title="Close Delete" onClick={closeEditor}>
                    Don't Delete
                </ButtonDefault>
                <ButtonDanger
                    title="Confirm Delete"
                    onClick={() => deleteNavigationElement(editingId)}
                >
                    Delete
                </ButtonDanger>
            </>
        );
    };

    return showingEditPage.isOpen && showingEditPage.navigationType !== null ? (
        <NavigationEditor
            navigationElementId={editingId}
            flowId={flowId}
            close={closeEditor}
            closeModal={close}
            done={refreshData}
            container={container}
            mapElements={mapElements}
            navigationType={showingEditPage.navigationType}
        />
    ) : (
        <Modal
            className="config-modal"
            renderHeader={showingDeletePage ? deletePageHeader : mainPageHeader}
            renderBody={showingDeletePage ? deletePageBody : mainPageBody}
            renderFooter={showingDeletePage ? deletePageFooter : mainPageFooter}
            container={container}
            onHide={close}
        />
    );
};

export default NavigationModal;
